import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Chip,
  Paper,
  useTheme,
  useMediaQuery,
  Switch,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Add,
  Delete,
  TableChart,
  Send,
  Close,
  Download,
} from '@mui/icons-material';
import axios from 'axios';

interface Fitter {
  id: number;
  name: string;
  email: string | null;
  specialty: string;
  is_active: number;
}

interface DiaryEntry {
  id: number;
  fitter_id: number;
  entry_date: string;
  customer_name: string | null;
  postcode: string | null;
  order_ref: string | null;
  price: number | null;
  details: string | null;
  duration: string | null;
  gas_required: 'YES' | 'NO';
  confirmed: number;
  rubbish_collected: number;
  entry_type: string;
  notes: string | null;
  fitter_name?: string;
  specialty?: string;
}

interface WasteEntry {
  id: number;
  entry_date: string;
  customer_name: string | null;
  customer_address: string | null;
  fitter_name: string | null;
  contact_number: string | null;
  is_paid: number;
  notes: string | null;
}

interface RemedialOldEntry {
  id: number;
  fitter_name: string | null;
  confirmation: string | null;
  customer_name: string | null;
  address: string | null;
  issue: string | null;
  status: string | null;
  phone: string | null;
  order_ref: string | null;
}

// Get the Monday of the week containing the given date
function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const DAY_NAMES = ['M', 'T', 'W', 'T', 'F', 'S'];
const DAY_FULL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const DURATIONS = ['HALF DAY', '1 DAY', '1.5 DAYS', '2 DAYS', '2.5 DAYS', '3 DAYS', '4 DAYS', '5 DAYS'];

const SPECIAL_ENTRIES = ['CANT WORK', 'REMEDIALS', 'LEAVE FREE', 'HOLIDAY', 'SHOWROOM', 'KEEP FREE', 'NOT WORKING', 'NO WORK', 'BANK HOLIDAY', 'GOOD FRIDAY', 'FREE DAY', 'XMAS EVE', 'XMAS DAY', 'BOXING DAY', 'NEW YEARS DAY', 'NEW YEARS EVE'];

// Tab definitions matching the exact spreadsheet tab names and order
const TABS = [
  { label: 'TOMMY', fitterName: 'Tommy', type: 'fitter' },
  { label: 'Alan', fitterName: 'Alan', type: 'fitter' },
  { label: 'Richard - GAS', fitterName: 'Richard Hague', type: 'fitter' },
  { label: 'Westminster', fitterName: 'Westminster', type: 'fitter' },
  { label: 'WASTE REMOVALS', fitterName: null, type: 'waste' },
  { label: '.', fitterName: null, type: 'remedial_old' },
];

export default function FittersDiary() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [currentTab, setCurrentTab] = useState(0);
  const [weekStart, setWeekStart] = useState<Date>(getMonday(new Date()));
  const [fitters, setFitters] = useState<Fitter[]>([]);
  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [wasteEntries, setWasteEntries] = useState<WasteEntry[]>([]);
  const [remedialOldEntries, setRemedialOldEntries] = useState<RemedialOldEntry[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [wasteDialogOpen, setWasteDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<DiaryEntry | null>(null);
  const [editWaste, setEditWaste] = useState<WasteEntry | null>(null);
  const [selectedFitter, setSelectedFitter] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'diary' | 'waste'; id: number } | null>(null);
  // Number of weeks to display
  const [weeksToShow, setWeeksToShow] = useState(4);

  // Export preview modal state
  const [exportPreviewOpen, setExportPreviewOpen] = useState(false);
  const [exportWeeks, setExportWeeks] = useState(4);
  const [exportStartDate, setExportStartDate] = useState<Date>(getMonday(new Date()));

  // Computed export date range
  const exportDateRange = useMemo(() => {
    const start = new Date(exportStartDate);
    const end = new Date(exportStartDate);
    end.setDate(end.getDate() + (exportWeeks * 7) - 1);
    return { start: formatDate(start), end: formatDate(end) };
  }, [exportStartDate, exportWeeks]);

  const [exportFitterId, setExportFitterId] = useState<string>('all');

  const handleOpenExportPreview = () => {
    setExportStartDate(new Date(weekStart));
    setExportWeeks(weeksToShow);
    setExportFitterId('all');
    setExportPreviewOpen(true);
  };

  // Email modal state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: '',
    cc: 'gordonsmakeovers@hotmail.com; info@gordonsmakeovers.co.uk',
    subject: `Fitters Diary - ${new Date().toLocaleDateString('en-GB')}`,
    message: 'Please find attached the current Fitters Diary.\n\nThanks,\nGordons Makeovers',
  });
  const [sending, setSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const excelFilename = `Fitters_Diary_${today}.xlsx`;

  const handleOpenEmailModal = () => {
    setExportPreviewOpen(false);
    setEmailForm({
      to: '',
      cc: 'gordonsmakeovers@hotmail.com; info@gordonsmakeovers.co.uk',
      subject: `Fitters Diary - ${new Date().toLocaleDateString('en-GB')}`,
      message: 'Please find attached the current Fitters Diary.\n\nThanks,\nGordons Makeovers',
    });
    setEmailError(null);
    setEmailSuccess(false);
    setEmailOpen(true);
  };

  const handleDownloadExcel = async () => {
    const token = localStorage.getItem('token');
    const fitterParam = exportFitterId !== 'all' ? `&fitter_id=${exportFitterId}` : '';
    const res = await fetch(`/api/fitters-diary/export-excel?start_date=${exportDateRange.start}&end_date=${exportDateRange.end}${fitterParam}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = excelFilename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSendEmail = async () => {
    if (!emailForm.to.trim()) { setEmailError('Please enter a recipient email address.'); return; }
    if (!emailForm.subject.trim()) { setEmailError('Please enter a subject.'); return; }
    setSending(true);
    setEmailError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/fitters-diary/send-excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...emailForm, start_date: exportDateRange.start, end_date: exportDateRange.end, fitter_id: exportFitterId !== 'all' ? exportFitterId : undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send');
      setEmailSuccess(true);
      setTimeout(() => setEmailOpen(false), 2000);
    } catch (err: any) {
      setEmailError(err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Form state for diary entry
  const [form, setForm] = useState({
    customer_name: '',
    postcode: '',
    order_ref: '',
    price: '',
    details: '',
    duration: '',
    gas_required: 'NO' as 'YES' | 'NO',
    confirmed: false,
    rubbish_collected: false,
    entry_type: 'job',
    notes: '',
  });

  // Form state for waste entry
  const [wasteForm, setWasteForm] = useState({
    customer_name: '',
    customer_address: '',
    fitter_name: '',
    contact_number: '',
    is_paid: false,
    notes: '',
  });

  // Calculate date range for all visible weeks
  const dateRange = useMemo(() => {
    const start = new Date(weekStart);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + (weeksToShow * 7) - 1);
    return { start: formatDate(start), end: formatDate(end) };
  }, [weekStart, weeksToShow]);

  // Generate weeks array
  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let w = 0; w < weeksToShow; w++) {
      const weekDays: Date[] = [];
      for (let d = 0; d < 6; d++) { // Mon-Sat only
        const day = new Date(weekStart);
        day.setDate(day.getDate() + (w * 7) + d);
        weekDays.push(day);
      }
      result.push(weekDays);
    }
    return result;
  }, [weekStart, weeksToShow]);

  // Fetch fitters
  const fetchFitters = useCallback(async () => {
    try {
      const res = await axios.get('/api/fitters/active');
      setFitters(res.data);
    } catch (err) {
      console.error('Error fetching fitters:', err);
    }
  }, []);

  // Fetch diary entries
  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`/api/fitters-diary/entries?start_date=${dateRange.start}&end_date=${dateRange.end}`);
      setEntries(res.data);
    } catch (err) {
      console.error('Error fetching entries:', err);
    }
  }, [dateRange]);

  // Fetch waste entries
  const fetchWasteEntries = useCallback(async () => {
    try {
      const res = await axios.get(`/api/fitters-diary/waste-removals?start_date=${dateRange.start}&end_date=${dateRange.end}`);
      setWasteEntries(res.data);
    } catch (err) {
      console.error('Error fetching waste entries:', err);
    }
  }, [dateRange]);

  useEffect(() => { fetchFitters(); }, [fetchFitters]);
  // Fetch remedial old entries
  const fetchRemedialOld = useCallback(async () => {
    try {
      const res = await axios.get('/api/fitters-diary/remedial-old');
      setRemedialOldEntries(res.data);
    } catch (err) {
      console.error('Error fetching remedial old entries:', err);
    }
  }, []);

  useEffect(() => { fetchEntries(); fetchWasteEntries(); }, [fetchEntries, fetchWasteEntries]);
  useEffect(() => { fetchRemedialOld(); }, [fetchRemedialOld]);

  // Get the fitter for the current tab
  const currentFitter = useMemo(() => {
    const tab = TABS[currentTab];
    if (!tab || tab.type === 'waste') return null;
    return fitters.find(f => f.name === tab.fitterName) || null;
  }, [fitters, currentTab]);

  // Get entries for a specific fitter and date (supports multiple per day)
  const getEntries = useCallback((fitterId: number, date: string): DiaryEntry[] => {
    return entries.filter(e => e.fitter_id === fitterId && e.entry_date.split('T')[0] === date);
  }, [entries]);

  // Get waste entries for a specific date
  const getWasteEntriesForDate = useCallback((date: string): WasteEntry[] => {
    return wasteEntries.filter(e => e.entry_date.split('T')[0] === date);
  }, [wasteEntries]);

  // Navigation
  const goToday = () => setWeekStart(getMonday(new Date()));
  const goPrevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };
  const goNextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };

  // Open dialog to add/edit diary entry
  const openDiaryDialog = (fitterId: number, date: string, entry?: DiaryEntry) => {
    setSelectedFitter(fitterId);
    setSelectedDate(date);
    if (entry) {
      setEditEntry(entry);
      setForm({
        customer_name: entry.customer_name || '',
        postcode: entry.postcode || '',
        order_ref: entry.order_ref || '',
        price: entry.price ? String(entry.price) : '',
        details: entry.details || '',
        duration: entry.duration || '',
        gas_required: entry.gas_required || 'NO',
        confirmed: !!entry.confirmed,
        rubbish_collected: !!entry.rubbish_collected,
        entry_type: entry.entry_type || 'job',
        notes: entry.notes || '',
      });
    } else {
      setEditEntry(null);
      setForm({
        customer_name: '',
        postcode: '',
        order_ref: '',
        price: '',
        details: '',
        duration: '',
        gas_required: 'NO',
        confirmed: false,
        rubbish_collected: false,
        entry_type: 'job',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  // Open dialog for waste entry
  const openWasteDialog = (date: string, entry?: WasteEntry) => {
    setSelectedDate(date);
    if (entry) {
      setEditWaste(entry);
      setWasteForm({
        customer_name: entry.customer_name || '',
        customer_address: entry.customer_address || '',
        fitter_name: entry.fitter_name || '',
        contact_number: entry.contact_number || '',
        is_paid: !!entry.is_paid,
        notes: entry.notes || '',
      });
    } else {
      setEditWaste(null);
      setWasteForm({
        customer_name: '',
        customer_address: '',
        fitter_name: '',
        contact_number: '',
        is_paid: false,
        notes: '',
      });
    }
    setWasteDialogOpen(true);
  };

  // Save diary entry
  const saveDiaryEntry = async () => {
    try {
      const data = {
        fitter_id: selectedFitter,
        entry_date: selectedDate,
        ...form,
        price: form.price ? parseFloat(form.price) : null,
      };

      if (editEntry) {
        await axios.put(`/api/fitters-diary/entries/${editEntry.id}`, data);
      } else {
        await axios.post('/api/fitters-diary/entries', data);
      }
      setDialogOpen(false);
      fetchEntries();
    } catch (err) {
      console.error('Error saving entry:', err);
    }
  };

  // Save waste entry
  const saveWasteEntry = async () => {
    try {
      const data = {
        entry_date: selectedDate,
        ...wasteForm,
      };

      if (editWaste) {
        await axios.put(`/api/fitters-diary/waste-removals/${editWaste.id}`, data);
      } else {
        await axios.post('/api/fitters-diary/waste-removals', data);
      }
      setWasteDialogOpen(false);
      fetchWasteEntries();
    } catch (err) {
      console.error('Error saving waste entry:', err);
    }
  };

  // Delete entry
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'diary') {
        await axios.delete(`/api/fitters-diary/entries/${deleteTarget.id}`);
        fetchEntries();
      } else {
        await axios.delete(`/api/fitters-diary/waste-removals/${deleteTarget.id}`);
        fetchWasteEntries();
      }
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Error deleting entry:', err);
    }
  };

  // Get month letter for a date (vertical month display like the spreadsheet)
  const getMonthDisplay = (days: Date[]): string[] => {
    // Show the month abbreviation for the first day of the week
    const month = MONTH_NAMES[days[0].getMonth()];
    const letters = month.split('');
    // Pad to 6 characters (Mon-Sat)
    while (letters.length < 6) letters.push('');
    return letters;
  };

  // Check if a date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  // Get cell background color based on entry type
  const getCellBg = (entry?: DiaryEntry): string => {
    if (!entry) return 'transparent';
    const t = entry.entry_type;
    if (t === 'CANT WORK' || t === 'HOLIDAY' || t === 'NOT WORKING' || t === 'NO WORK') return '#ffebee';
    if (t === 'LEAVE FREE' || t === 'KEEP FREE' || t === 'FREE DAY') return '#fff3e0';
    if (t === 'REMEDIALS') return '#e8f5e9';
    if (t === 'SHOWROOM') return '#e3f2fd';
    if (t === 'BANK HOLIDAY' || t === 'GOOD FRIDAY' || t === 'XMAS EVE' || t === 'XMAS DAY' || t === 'BOXING DAY' || t === 'NEW YEARS DAY' || t === 'NEW YEARS EVE') return '#f3e5f5';
    if (entry.confirmed) return '#f1f8e9';
    return 'transparent';
  };

  // Render the spreadsheet-style table for kitchen/gas fitters
  const renderFitterTable = (fitter: Fitter) => {
    const isGas = fitter.specialty === 'Gas Fitter';

    return (
      <Box key={fitter.id} sx={{ mb: 4 }}>
        {/* Fitter Header - matches the spreadsheet header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#2c3e50',
          color: 'white',
          px: 2,
          py: 1,
          borderRadius: '4px 4px 0 0',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
            FITTER{isGas ? '' : 'S'}: {fitter.name.toUpperCase()}
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            {fitter.specialty}
          </Typography>
        </Box>

        {/* Column Headers */}
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: '"Roboto Mono", "Courier New", monospace',
            fontSize: isMobile ? '11px' : '13px',
            minWidth: isGas ? '900px' : '1000px',
          }}>
            <thead>
              <tr style={{ backgroundColor: '#ecf0f1', borderBottom: '2px solid #bdc3c7' }}>
                <th style={{ ...thStyle, width: '25px' }}></th>
                <th style={{ ...thStyle, width: '30px' }}>DAY</th>
                <th style={{ ...thStyle, width: '25px' }}></th>
                <th style={{ ...thStyle, width: '140px', textAlign: 'left' }}>NAME</th>
                <th style={{ ...thStyle, width: '25px' }}>{isGas ? '' : '✓'}</th>
                <th style={{ ...thStyle, width: '60px' }}>P.CODE</th>
                <th style={{ ...thStyle, width: '65px' }}>REF.</th>
                <th style={{ ...thStyle, width: '60px' }}>PRICE</th>
                <th style={{ ...thStyle, width: '200px', textAlign: 'left' }}>DETAILS</th>
                <th style={{ ...thStyle, width: '80px' }}>{isGas ? '' : 'DURATION'}</th>
                <th style={{ ...thStyle, width: '40px' }}>GAS</th>
                <th style={{ ...thStyle, width: '30px' }}>**</th>
                <th style={{ ...thStyle, width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((weekDays, weekIdx) => {
                const monthLetters = getMonthDisplay(weekDays);
                return (
                  <React.Fragment key={weekIdx}>
                    {weekDays.map((day, dayIdx) => {
                      const dateStr = formatDate(day);
                      const dayEntries = getEntries(fitter.id, dateStr);
                      const todayBg = isToday(day) ? '#fffde7' : 'white';

                      // No entries - show empty row
                      if (dayEntries.length === 0) {
                        return (
                          <tr
                            key={dateStr}
                            style={{ backgroundColor: todayBg, borderBottom: '1px solid #e0e0e0', cursor: 'pointer' }}
                            onClick={() => openDiaryDialog(fitter.id, dateStr)}
                          >
                            <td style={{ ...tdStyle, fontWeight: 'bold', color: '#e74c3c', textAlign: 'center', fontSize: '12px' }}>{monthLetters[dayIdx]}</td>
                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>{day.getDate()}</td>
                            <td style={{ ...tdStyle, textAlign: 'center', color: dayIdx === 5 ? '#e74c3c' : '#666' }}>{DAY_NAMES[dayIdx]}</td>
                            <td style={tdStyle}></td><td style={tdStyle}></td><td style={tdStyle}></td>
                            <td style={tdStyle}></td><td style={tdStyle}></td><td style={tdStyle}></td>
                            <td style={tdStyle}></td><td style={tdStyle}></td><td style={tdStyle}></td>
                            <td style={{ ...tdStyle, padding: '1px' }}></td>
                          </tr>
                        );
                      }

                      // One or more entries
                      return dayEntries.map((entry, entryIdx) => {
                        const bg = getCellBg(entry);
                        const rowBg = isToday(day) ? '#fffde7' : bg || 'white';
                        const isSpecial = SPECIAL_ENTRIES.includes(entry.entry_type);

                        return (
                          <tr
                            key={`${dateStr}-${entry.id}`}
                            style={{ backgroundColor: rowBg, borderBottom: '1px solid #e0e0e0', cursor: 'pointer' }}
                            onClick={() => openDiaryDialog(fitter.id, dateStr, entry)}
                          >
                            {entryIdx === 0 ? (
                              <>
                                <td style={{ ...tdStyle, fontWeight: 'bold', color: '#e74c3c', textAlign: 'center', fontSize: '12px' }}>{monthLetters[dayIdx]}</td>
                                <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>{day.getDate()}</td>
                                <td style={{ ...tdStyle, textAlign: 'center', color: dayIdx === 5 ? '#e74c3c' : '#666' }}>{DAY_NAMES[dayIdx]}</td>
                              </>
                            ) : (
                              <>
                                <td style={tdStyle}></td>
                                <td style={tdStyle}></td>
                                <td style={tdStyle}></td>
                              </>
                            )}
                            <td style={{ ...tdStyle, fontWeight: 'bold', color: isSpecial ? '#e74c3c' : '#333', textTransform: 'uppercase' }}>
                              {isSpecial ? entry.entry_type : (entry.customer_name?.toUpperCase() || '')}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center', color: '#27ae60' }}>{entry.confirmed ? '/' : ''}</td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>{entry.postcode?.toUpperCase() || ''}</td>
                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: entry.order_ref ? 'bold' : 'normal' }}>{entry.order_ref || ''}</td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>{entry.price ? `£${Number(entry.price).toFixed(0)}` : ''}</td>
                            <td style={{ ...tdStyle, textTransform: 'uppercase', fontSize: isMobile ? '10px' : '12px', whiteSpace: 'pre-line' }}>
                              {isSpecial ? (entry.notes || '') : (entry.details || '')}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center', fontSize: '11px' }}>{!isGas ? (entry.duration || '') : ''}</td>
                            <td style={{ ...tdStyle, textAlign: 'center', color: entry.gas_required === 'YES' ? '#e74c3c' : '#27ae60', fontWeight: 'bold' }}>
                              {entry.gas_required === 'YES' ? 'YES' : 'NO'}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center', color: '#e74c3c', fontWeight: 'bold' }}>
                              {!entry.rubbish_collected && entry.customer_name ? '**' : ''}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center', padding: '1px', whiteSpace: 'nowrap' }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteTarget({ type: 'diary', id: entry.id });
                                  setDeleteConfirmOpen(true);
                                }}
                                sx={{ padding: '2px' }}
                              >
                                <Delete sx={{ fontSize: 14, color: '#999' }} />
                              </IconButton>
                              {entryIdx === dayEntries.length - 1 && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); openDiaryDialog(fitter.id, dateStr); }}
                                  sx={{ padding: '2px' }}
                                >
                                  <Add sx={{ fontSize: 14, color: '#999' }} />
                                </IconButton>
                              )}
                            </td>
                          </tr>
                        );
                      });
                    })}
                    {/* Blank separator row between weeks */}
                    {weekIdx < weeks.length - 1 && (
                      <tr>
                        <td colSpan={13} style={{ height: '8px', backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}></td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Box>
    );
  };

  // Render waste removal table
  const renderWasteTable = () => {
    return (
      <Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#2c3e50',
          color: 'white',
          px: 2,
          py: 1,
          borderRadius: '4px 4px 0 0',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
            WASTE REMOVALS - JOE (ANY WASTE)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip size="small" label="YELLOW = PAID" sx={{ backgroundColor: '#FFFF00', color: '#333', fontWeight: 'bold', fontSize: '10px' }} />
          </Box>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: '"Roboto Mono", "Courier New", monospace',
            fontSize: isMobile ? '11px' : '13px',
            minWidth: '900px',
          }}>
            <thead>
              <tr style={{ backgroundColor: '#ecf0f1', borderBottom: '2px solid #bdc3c7' }}>
                <th style={{ ...thStyle, width: '25px' }}></th>
                <th style={{ ...thStyle, width: '30px' }}>DAY</th>
                <th style={{ ...thStyle, width: '30px' }}></th>
                <th style={{ ...thStyle, width: '140px', textAlign: 'left' }}>CUSTOMER</th>
                <th style={{ ...thStyle, width: '280px', textAlign: 'left' }}>CUSTOMER ADDRESS</th>
                <th style={{ ...thStyle, width: '120px', textAlign: 'left' }}>FITTER</th>
                <th style={{ ...thStyle, width: '130px' }}>CONTACT NUMBER</th>
                <th style={{ ...thStyle, width: '50px' }}>PAID</th>
                <th style={{ ...thStyle, width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((weekDays, weekIdx) => {
                const monthLetters = getMonthDisplay(weekDays);
                return (
                  <React.Fragment key={weekIdx}>
                    {weekDays.map((day, dayIdx) => {
                      const dateStr = formatDate(day);
                      const dayEntries = getWasteEntriesForDate(dateStr);
                      const todayHighlight = isToday(day) ? '#fffde7' : 'white';

                      if (dayEntries.length === 0) {
                        return (
                          <tr
                            key={dateStr}
                            style={{ backgroundColor: todayHighlight, borderBottom: '1px solid #e0e0e0', cursor: 'pointer' }}
                            onClick={() => openWasteDialog(dateStr)}
                          >
                            <td style={{ ...tdStyle, fontWeight: 'bold', color: '#e74c3c', textAlign: 'center', fontSize: '12px' }}>{monthLetters[dayIdx]}</td>
                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>{day.getDate()}</td>
                            <td style={{ ...tdStyle, textAlign: 'center', color: dayIdx === 5 ? '#e74c3c' : '#666' }}>{DAY_NAMES[dayIdx]}</td>
                            <td style={tdStyle}></td>
                            <td style={tdStyle}></td>
                            <td style={tdStyle}></td>
                            <td style={tdStyle}></td>
                            <td style={tdStyle}></td>
                            <td style={{ ...tdStyle, padding: '1px' }}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); openWasteDialog(dateStr); }} sx={{ padding: '2px' }}>
                                <Add sx={{ fontSize: 14, color: '#999' }} />
                              </IconButton>
                            </td>
                          </tr>
                        );
                      }

                      return dayEntries.map((entry, entryIdx) => (
                        <tr
                          key={`${dateStr}-${entry.id}`}
                          style={{
                            backgroundColor: entry.is_paid ? '#FFFF00' : todayHighlight,
                            borderBottom: '1px solid #e0e0e0',
                            cursor: 'pointer',
                          }}
                          onClick={() => openWasteDialog(dateStr, entry)}
                        >
                          {entryIdx === 0 ? (
                            <>
                              <td style={{ ...tdStyle, fontWeight: 'bold', color: '#e74c3c', textAlign: 'center', fontSize: '12px' }}>{monthLetters[dayIdx]}</td>
                              <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>{day.getDate()}</td>
                              <td style={{ ...tdStyle, textAlign: 'center', color: dayIdx === 5 ? '#e74c3c' : '#666' }}>{DAY_NAMES[dayIdx]}</td>
                            </>
                          ) : (
                            <>
                              <td style={tdStyle}></td>
                              <td style={tdStyle}></td>
                              <td style={tdStyle}></td>
                            </>
                          )}
                          <td style={{ ...tdStyle, textTransform: 'uppercase', fontWeight: 'bold' }}>{entry.customer_name || ''}</td>
                          <td style={{ ...tdStyle, fontSize: '11px' }}>{entry.customer_address || ''}</td>
                          <td style={{ ...tdStyle, textTransform: 'uppercase' }}>{entry.fitter_name || ''}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>{entry.contact_number || ''}</td>
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>
                            {entry.is_paid ? 'PAID' : ''}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center', padding: '1px', whiteSpace: 'nowrap' }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget({ type: 'waste', id: entry.id });
                                setDeleteConfirmOpen(true);
                              }}
                              sx={{ padding: '2px' }}
                            >
                              <Delete sx={{ fontSize: 14, color: '#999' }} />
                            </IconButton>
                            {entryIdx === dayEntries.length - 1 && (
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); openWasteDialog(dateStr); }}
                                sx={{ padding: '2px' }}
                              >
                                <Add sx={{ fontSize: 14, color: '#999' }} />
                              </IconButton>
                            )}
                          </td>
                        </tr>
                      ));
                    })}
                    {weekIdx < weeks.length - 1 && (
                      <tr>
                        <td colSpan={9} style={{ height: '8px', backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}></td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Header row: title + nav */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
          Fitters Diary
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<TableChart />}
            onClick={handleOpenExportPreview}
            sx={{ backgroundColor: '#27ae60', '&:hover': { backgroundColor: '#219150' }, mr: 1, fontSize: { xs: '0.65rem', md: '0.8rem' } }}
          >
            Export &amp; Email
          </Button>
          <IconButton onClick={goPrevWeek} size="small"><ChevronLeft /></IconButton>
          <Button variant="outlined" size="small" onClick={goToday} sx={{ minWidth: 'auto', px: 1, fontSize: '0.75rem' }}>
            Today
          </Button>
          <IconButton onClick={goNextWeek} size="small"><ChevronRight /></IconButton>
        </Box>
      </Box>

      {/* Date range + weeks selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          {' - '}
          {(() => {
            const end = new Date(weekStart);
            end.setDate(end.getDate() + (weeksToShow * 7) - 2);
            return end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
          })()}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel>View</InputLabel>
          <Select
            value={weeksToShow}
            label="View"
            onChange={(e) => setWeeksToShow(Number(e.target.value))}
            sx={{ fontSize: '0.8rem' }}
          >
            <MenuItem value={1}>1 Week</MenuItem>
            <MenuItem value={2}>2 Weeks</MenuItem>
            <MenuItem value={4}>4 Weeks</MenuItem>
            <MenuItem value={6}>6 Weeks</MenuItem>
            <MenuItem value={8}>8 Weeks</MenuItem>
            <MenuItem value={12}>12 Weeks</MenuItem>
            <MenuItem value={26}>6 Months</MenuItem>
            <MenuItem value={52}>1 Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tabs — stacked 2-per-row on mobile, full-width tab bar on desktop */}
      <Paper sx={{ mb: 2, overflow: 'hidden' }}>
        {isMobile ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 0.5 }}>
            {TABS.map((tab, idx) => (
              <Button
                key={idx}
                onClick={() => setCurrentTab(idx)}
                variant={currentTab === idx ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  flex: tab.label === '.' ? '0 0 44px' : '1 1 calc(50% - 4px)',
                  minWidth: 0,
                  fontWeight: 'bold',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                  py: 0.75,
                }}
              >
                {tab.label === '.' ? '•' : tab.label}
              </Button>
            ))}
          </Box>
        ) : (
          <Tabs
            value={currentTab}
            onChange={(_, v) => setCurrentTab(v)}
            variant="fullWidth"
            TabIndicatorProps={{ sx: { height: 3 } }}
            sx={{
              minHeight: 40,
              '& .MuiTabs-flexContainer': {
                width: '100%',
              },
              '& .MuiTab-root': {
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: { xs: '0.7rem', md: '0.85rem' },
                minWidth: 0,
                minHeight: 40,
                px: { xs: 0.5, md: 2 },
                py: 1,
              },
            }}
          >
            {TABS.map((tab, idx) => (
              <Tab key={idx} label={tab.label} sx={tab.label === '.' ? { maxWidth: { xs: 30, md: 'none' }, flex: { xs: '0 0 30px', md: 1 } } : {}} />
            ))}
          </Tabs>
        )}
      </Paper>

      {/* Content - one fitter per tab, waste removals, remedial old */}
      {TABS[currentTab]?.type === 'fitter' && currentFitter && renderFitterTable(currentFitter)}
      {TABS[currentTab]?.type === 'waste' && renderWasteTable()}
      {TABS[currentTab]?.type === 'remedial_old' && (
        <Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#2c3e50',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: '4px 4px 0 0',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
              .
            </Typography>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: '"Roboto Mono", "Courier New", monospace',
              fontSize: isMobile ? '11px' : '13px',
              minWidth: '1100px',
            }}>
              <thead>
                <tr style={{ backgroundColor: '#ecf0f1', borderBottom: '2px solid #bdc3c7' }}>
                  <th style={{ ...thStyle, width: '140px', textAlign: 'left' }}>FITTER</th>
                  <th style={{ ...thStyle, width: '250px', textAlign: 'left' }}>CONFIRMED</th>
                  <th style={{ ...thStyle, width: '100px', textAlign: 'left' }}>CUSTOMER</th>
                  <th style={{ ...thStyle, width: '220px', textAlign: 'left' }}>ADDRESS</th>
                  <th style={{ ...thStyle, width: '250px', textAlign: 'left' }}>ISSUE</th>
                  <th style={{ ...thStyle, width: '120px', textAlign: 'left' }}>STATUS</th>
                  <th style={{ ...thStyle, width: '160px' }}>PHONE</th>
                  <th style={{ ...thStyle, width: '70px' }}>REF</th>
                </tr>
              </thead>
              <tbody>
                {remedialOldEntries.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td style={{ ...tdStyle, textTransform: 'uppercase', fontWeight: 'bold' }}>{entry.fitter_name || ''}</td>
                    <td style={{ ...tdStyle, fontSize: '11px' }}>{entry.confirmation || ''}</td>
                    <td style={{ ...tdStyle, textTransform: 'uppercase', fontWeight: 'bold' }}>{entry.customer_name || ''}</td>
                    <td style={{ ...tdStyle, fontSize: '11px' }}>{entry.address || ''}</td>
                    <td style={{ ...tdStyle, fontSize: '11px' }}>{entry.issue || ''}</td>
                    <td style={{ ...tdStyle, fontSize: '11px' }}>{entry.status || ''}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '11px' }}>{entry.phone || ''}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>{entry.order_ref || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Diary Entry Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {editEntry ? 'Edit Diary Entry' : 'Add Diary Entry'}
          <Typography variant="caption" display="block" color="text.secondary">
            {selectedDate && new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            {' - '}
            {fitters.find(f => f.id === selectedFitter)?.name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Entry type */}
            <FormControl size="small" fullWidth>
              <InputLabel>Entry Type</InputLabel>
              <Select
                value={form.entry_type}
                label="Entry Type"
                onChange={(e) => setForm({ ...form, entry_type: e.target.value })}
              >
                <MenuItem value="job">Job</MenuItem>
                {SPECIAL_ENTRIES.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {form.entry_type === 'job' && (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Customer Name"
                    size="small"
                    fullWidth
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                  <TextField
                    label="Postcode"
                    size="small"
                    sx={{ width: '120px' }}
                    value={form.postcode}
                    onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Order Ref"
                    size="small"
                    sx={{ width: '130px' }}
                    value={form.order_ref}
                    onChange={(e) => setForm({ ...form, order_ref: e.target.value })}
                  />
                  <TextField
                    label="Price (£)"
                    size="small"
                    type="number"
                    sx={{ width: '120px' }}
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                  />
                  <FormControl size="small" sx={{ width: '140px' }}>
                    <InputLabel>Duration</InputLabel>
                    <Select
                      value={form.duration}
                      label="Duration"
                      onChange={(e) => setForm({ ...form, duration: e.target.value })}
                    >
                      <MenuItem value="">-</MenuItem>
                      {DURATIONS.map(d => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <TextField
                  label="Details (door style / description)"
                  size="small"
                  fullWidth
                  value={form.details}
                  onChange={(e) => setForm({ ...form, details: e.target.value })}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ width: '100px' }}>
                    <InputLabel>Gas</InputLabel>
                    <Select
                      value={form.gas_required}
                      label="Gas"
                      onChange={(e) => setForm({ ...form, gas_required: e.target.value as 'YES' | 'NO' })}
                    >
                      <MenuItem value="YES">YES</MenuItem>
                      <MenuItem value="NO">NO</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant={form.confirmed ? 'contained' : 'outlined'}
                    color={form.confirmed ? 'success' : 'inherit'}
                    size="small"
                    onClick={() => setForm({ ...form, confirmed: !form.confirmed })}
                  >
                    Confirmed {form.confirmed ? '✓' : ''}
                  </Button>
                  <Button
                    variant={form.rubbish_collected ? 'contained' : 'outlined'}
                    color={form.rubbish_collected ? 'success' : 'inherit'}
                    size="small"
                    onClick={() => setForm({ ...form, rubbish_collected: !form.rubbish_collected })}
                  >
                    Rubbish Collected {form.rubbish_collected ? '✓' : ''}
                  </Button>
                </Box>
              </>
            )}

            <TextField
              label="Notes"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveDiaryEntry}>
            {editEntry ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Waste Entry Dialog */}
      <Dialog open={wasteDialogOpen} onClose={() => setWasteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {editWaste ? 'Edit Waste Removal' : 'Add Waste Removal'}
          <Typography variant="caption" display="block" color="text.secondary">
            {selectedDate && new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Customer Name"
              size="small"
              fullWidth
              value={wasteForm.customer_name}
              onChange={(e) => setWasteForm({ ...wasteForm, customer_name: e.target.value })}
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
            <TextField
              label="Customer Address"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={wasteForm.customer_address}
              onChange={(e) => setWasteForm({ ...wasteForm, customer_address: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Fitter</InputLabel>
                <Select
                  value={wasteForm.fitter_name}
                  label="Fitter"
                  onChange={(e) => setWasteForm({ ...wasteForm, fitter_name: e.target.value })}
                >
                  <MenuItem value="">-</MenuItem>
                  {fitters.filter(f => f.specialty === 'Kitchen Fitter').map(f => (
                    <MenuItem key={f.id} value={f.name}>{f.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Contact Number"
                size="small"
                fullWidth
                value={wasteForm.contact_number}
                onChange={(e) => setWasteForm({ ...wasteForm, contact_number: e.target.value })}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={wasteForm.is_paid}
                  onChange={(e) => setWasteForm({ ...wasteForm, is_paid: e.target.checked })}
                  color="warning"
                />
              }
              label={<Typography sx={{ fontWeight: 'bold', color: wasteForm.is_paid ? '#f39c12' : 'text.secondary' }}>PAID</Typography>}
            />
            <TextField
              label="Notes"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={wasteForm.notes}
              onChange={(e) => setWasteForm({ ...wasteForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWasteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveWasteEntry}>
            {editWaste ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Entry?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this entry?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Export Preview Modal */}
      <Dialog open={exportPreviewOpen} onClose={() => setExportPreviewOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableChart color="success" />
            <Typography variant="h6">Export Fitters Diary</Typography>
          </Box>
          <IconButton size="small" onClick={() => setExportPreviewOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Choose the date range for the exported Excel file.
            </Typography>

            {/* Start date picker */}
            <TextField
              label="Starting from (Monday)"
              type="date"
              size="small"
              fullWidth
              value={formatDate(exportStartDate)}
              onChange={(e) => {
                const d = new Date(e.target.value + 'T00:00:00');
                setExportStartDate(getMonday(d));
              }}
              InputLabelProps={{ shrink: true }}
            />

            {/* View / weeks dropdown */}
            <FormControl size="small" fullWidth>
              <InputLabel>View</InputLabel>
              <Select
                value={exportWeeks}
                label="View"
                onChange={(e) => setExportWeeks(Number(e.target.value))}
              >
                <MenuItem value={1}>1 Week</MenuItem>
                <MenuItem value={2}>2 Weeks</MenuItem>
                <MenuItem value={4}>4 Weeks</MenuItem>
                <MenuItem value={6}>6 Weeks</MenuItem>
                <MenuItem value={8}>8 Weeks</MenuItem>
                <MenuItem value={12}>12 Weeks</MenuItem>
                <MenuItem value={26}>6 Months</MenuItem>
                <MenuItem value={52}>1 Year</MenuItem>
              </Select>
            </FormControl>

            {/* Date range preview */}
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" fontWeight="bold">
                {new Date(exportDateRange.start + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                {' — '}
                {new Date(exportDateRange.end + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {exportWeeks} week{exportWeeks !== 1 ? 's' : ''} of diary data will be exported
              </Typography>
            </Box>

            {/* Fitter filter */}
            <FormControl size="small" fullWidth>
              <InputLabel>Fitter</InputLabel>
              <Select
                value={exportFitterId}
                label="Fitter"
                onChange={(e) => setExportFitterId(e.target.value)}
              >
                <MenuItem value="all">All Fitters</MenuItem>
                {fitters.filter(f => f.is_active).map(f => (
                  <MenuItem key={f.id} value={String(f.id)}>{f.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setExportPreviewOpen(false)} variant="outlined">Cancel</Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadExcel}
            >
              Download
            </Button>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={handleOpenEmailModal}
            >
              Email
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={emailOpen} onClose={() => !sending && setEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableChart color="success" />
            <Typography variant="h6">Email Fitters Diary</Typography>
          </Box>
          <IconButton size="small" onClick={() => setEmailOpen(false)} disabled={sending}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {emailSuccess ? (
            <Alert severity="success">Email sent successfully!</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Attachment info */}
              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableChart fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight="medium">{excelFilename}</Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<Download />}
                  sx={{ fontSize: '0.75rem' }}
                  onClick={handleDownloadExcel}
                >
                  Preview
                </Button>
              </Box>

              {/* Quick-add fitter emails */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Quick add fitter:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {fitters.filter(f => f.email).map(f => {
                    const email = f.email as string;
                    return (
                      <Chip
                        key={f.id}
                        label={f.name}
                        size="small"
                        clickable
                        disabled={sending}
                        color={emailForm.to.includes(email) ? 'primary' : 'default'}
                        variant={emailForm.to.includes(email) ? 'filled' : 'outlined'}
                        onClick={() => {
                          const current = emailForm.to.trim();
                          if (current.includes(email)) return;
                          const newTo = current ? `${current}; ${email}` : email;
                          setEmailForm({ ...emailForm, to: newTo });
                        }}
                        sx={{ fontSize: '0.75rem' }}
                      />
                    );
                  })}
                </Box>
              </Box>

              <TextField
                label="To"
                value={emailForm.to}
                onChange={e => setEmailForm({ ...emailForm, to: e.target.value })}
                fullWidth
                required
                disabled={sending}
                helperText="Separate multiple emails with semicolon (;)"
              />
              <TextField
                label="CC"
                value={emailForm.cc}
                onChange={e => setEmailForm({ ...emailForm, cc: e.target.value })}
                fullWidth
                disabled={sending}
                helperText="Separate multiple emails with semicolon (;)"
              />
              <TextField
                label="Subject"
                value={emailForm.subject}
                onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })}
                fullWidth
                required
                disabled={sending}
              />
              <TextField
                label="Message"
                value={emailForm.message}
                onChange={e => setEmailForm({ ...emailForm, message: e.target.value })}
                fullWidth
                multiline
                rows={4}
                disabled={sending}
              />
              {emailError && <Alert severity="error" onClose={() => setEmailError(null)}>{emailError}</Alert>}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEmailOpen(false)} disabled={sending} variant="outlined">Cancel</Button>
          <Button
            onClick={handleSendEmail}
            disabled={sending || emailSuccess}
            variant="contained"
            startIcon={sending ? <CircularProgress size={18} /> : <Send />}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Table styles matching spreadsheet look
const thStyle: React.CSSProperties = {
  padding: '6px 4px',
  textAlign: 'center',
  fontWeight: 'bold',
  fontSize: '11px',
  color: '#333',
  whiteSpace: 'nowrap',
  borderBottom: '2px solid #bdc3c7',
};

const tdStyle: React.CSSProperties = {
  padding: '4px 6px',
  borderBottom: '1px solid #e8e8e8',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
